// Dashboard 页（Task 16.3）——
//   - 1h / 24h / 7d / 30d 切换
//   - 通过 / 拒绝 / challenge 占比
//   - 60 RPM 限速由后端实施；前端在响应 429 时把按钮禁用 N 秒

import { useEffect, useState } from 'react';
import { ApiError, getDashboardMetrics, type DashboardMetrics } from '../lib/api';

export function Dashboard(): JSX.Element {
  const [range, setRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h');
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      try {
        const m = await getDashboardMetrics(range);
        if (!cancelled) setData(m);
      } catch (ex) {
        if (cancelled) return;
        if (ex instanceof ApiError && ex.code === 'RATE_LIMITED') {
          setErr('请求过于频繁（60 RPM 限速）');
        } else {
          setErr(ex instanceof Error ? ex.message : 'failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range]);

  return (
    <div>
      <h2>仪表盘</h2>

      <div style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
        {(['1h', '24h', '7d', '30d'] as const).map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            style={{
              ...rangeBtn,
              background: range === r ? '#2563eb' : 'white',
              color: range === r ? 'white' : '#475569',
              borderColor: range === r ? '#2563eb' : '#cbd5e1',
            }}
          >
            {r}
          </button>
        ))}
      </div>

      {err && <div style={errorBox}>{err}</div>}
      {loading && <div style={{ color: '#64748b' }}>加载中…</div>}

      {data && (
        <div>
          {data.realtime_lag_sec !== undefined && data.realtime_lag_sec > 60 && (
            <div style={{ ...errorBox, background: '#fef3c7', color: '#92400e' }}>
              实时数据延迟约 {data.realtime_lag_sec}s
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            <Card title="总验证次数" value={data.total.toLocaleString()} />
            <Card title="通过率" value={`${(data.pass_rate * 100).toFixed(2)}%`} accent="#10b981" />
            <Card title="挑战占比" value={`${(data.challenge_rate * 100).toFixed(2)}%`} accent="#f59e0b" />
            <Card title="拒绝率" value={`${(data.deny_rate * 100).toFixed(2)}%`} accent="#ef4444" />
          </div>

          <div style={{ marginTop: 24 }}>
            <ExportButtons range={range} data={data} />
          </div>
        </div>
      )}
    </div>
  );
}

function Card(props: { title: string; value: string; accent?: string }): JSX.Element {
  return (
    <div
      style={{
        padding: 16,
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        borderLeft: props.accent ? `4px solid ${props.accent}` : undefined,
      }}
    >
      <div style={{ fontSize: 12, color: '#64748b' }}>{props.title}</div>
      <div style={{ fontSize: 24, fontWeight: 600, marginTop: 6 }}>{props.value}</div>
    </div>
  );
}

function ExportButtons(props: { range: string; data: DashboardMetrics }): JSX.Element {
  function download(filename: string, content: string, mime: string): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  function exportJson(): void {
    download(`metrics-${props.range}.json`, JSON.stringify(props.data, null, 2), 'application/json');
  }
  function exportCsv(): void {
    const rows = [
      ['metric', 'value'],
      ['total', String(props.data.total)],
      ['pass', String(props.data.pass)],
      ['challenge', String(props.data.challenge)],
      ['deny', String(props.data.deny)],
      ['pass_rate', String(props.data.pass_rate)],
      ['challenge_rate', String(props.data.challenge_rate)],
      ['deny_rate', String(props.data.deny_rate)],
    ];
    download(
      `metrics-${props.range}.csv`,
      rows.map((r) => r.join(',')).join('\n'),
      'text/csv',
    );
  }
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button onClick={exportCsv} style={ghostBtn}>导出 CSV</button>
      <button onClick={exportJson} style={ghostBtn}>导出 JSON</button>
    </div>
  );
}

const rangeBtn: React.CSSProperties = {
  padding: '6px 12px',
  border: '1px solid',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer',
};
const errorBox: React.CSSProperties = {
  margin: '12px 0',
  padding: 8,
  fontSize: 12,
  color: '#991b1b',
  background: '#fee2e2',
  borderRadius: 6,
};
